import { Component, OnDestroy, OnInit } from '@angular/core';
import { Notification } from './components/notification/notification';
import { UserProfile } from './components/user-profile/user-profile';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { Role } from "./components/role/role";
import { TranslatePipe } from '../../pipes/translatePipe';
import { FormOfflineSyncIndicatorComponent } from './components/form-offline-sync-indicator-component/form-offline-sync-indicator-component';
import { AuthService } from '../../services/auth.service';
import { RoleTypeId } from '../../enums/role-type.enum';
import { getSidebarItems } from '../../constants/sidebar-items';
import { SidebarItem } from '../../models/sidebar-item.model';
import { filter, Subscription } from 'rxjs';

@Component({
  selector: 'app-header',
  imports: [Notification, UserProfile, RouterModule, Role, TranslatePipe, FormOfflineSyncIndicatorComponent],
  templateUrl: './header.html',
  styleUrl: './header.scss'
})
export class Header implements OnInit, OnDestroy {
  isSuperAdmin = false;
  roleTypeId: number | null = null;
  RoleTypeId = RoleTypeId;
  activePageLabel = 'Dashboard';

  private routerSubscription?: Subscription;
  private roleChangeHandler = (event: any) => {
    const role = event?.detail;
    this.setRoleTypeFromRole(role);
  };

  get dashboardRoute(): string {
    return this.isSuperAdmin ? '/admin/dashboard' : '/dashboard';
  }

  constructor(
    private authService: AuthService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.isSuperAdmin = this.authService.isAdminUser();

    const selectedRole = localStorage.getItem('selectedRole');
    if (selectedRole) {
      try {
        const role = JSON.parse(selectedRole);
        this.setRoleTypeFromRole(role);
      } catch {
        this.roleTypeId = null;
      }
    }

    this.updateActivePageLabel(this.router.url);
    this.routerSubscription = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      this.isSuperAdmin = this.authService.isAdminUser();
      this.updateActivePageLabel(event.urlAfterRedirects || event.url);
    });

    window.addEventListener('roleChanged', this.roleChangeHandler as EventListener);
  }

  ngOnDestroy(): void {
    window.removeEventListener('roleChanged', this.roleChangeHandler as EventListener);
    this.routerSubscription?.unsubscribe();
  }

  private setRoleTypeFromRole(role: any) {
    const rawRoleType = role?.role_type_id ?? null;
    this.roleTypeId =
      typeof rawRoleType === 'number' ? rawRoleType : rawRoleType ? Number(rawRoleType) : null;
  }

  private updateActivePageLabel(url: string): void {
    const path = url.split('?')[0].split('#')[0];
    const match = this.findBestSidebarMatch(path, getSidebarItems());
    this.activePageLabel = match?.label || this.formatPathLabel(path);
  }

  private findBestSidebarMatch(path: string, items: SidebarItem[]): SidebarItem | null {
    let best: SidebarItem | null = null;
    let bestLength = -1;

    const consider = (item: SidebarItem) => {
      const route = item.route?.split('?')[0];
      if (!route || /^https?:\/\//i.test(route)) return;
      if (path === route || path.startsWith(route + '/')) {
        if (route.length > bestLength) {
          best = item;
          bestLength = route.length;
        }
      }
    };

    for (const item of items) {
      consider(item);
      item.children?.forEach(consider);
    }

    return best;
  }

  private formatPathLabel(path: string): string {
    const segments = path.split('/').filter(Boolean);
    if (!segments.length) return 'Dashboard';

    const last = segments[segments.length - 1];
    if (/^\d+$/.test(last) && segments.length > 1) {
      return this.titleCase(segments[segments.length - 2]);
    }

    return this.titleCase(last);
  }

  private titleCase(value: string): string {
    return value
      .replace(/[-_]+/g, ' ')
      .replace(/\b\w/g, char => char.toUpperCase());
  }
}
